<template>
  <div class="c-library-book">
    <div
      class="c-library-book-download__status"
      role="status"
      aria-live="polite"
    >
      {{ bookStatusUpdate }}
    </div>
    <div class="c-library-book__image-wrapper">
      <div class="c-library-book__actions">
        <router-link :to="{ name: 'Book', params: { id: book.id }}" class="c-library-book__link c-library-book__link--image" :aria-label="`${$t('GO_TO')} ${book.title}`">
          <img crossorigin="anonymous" :alt="$t('BOOK_COVER')" v-lazy="book.cover" @load="findCoverSize($event)" class="c-library-book__image" />
          <div class="c-library-book__image--bookside"></div>
          <div class="c-library-book__image--bookline"></div>
          <div class="c-library-book__image--bookbottom"></div>
        </router-link>

        <div class="c-library-book__archive-radio" v-if="hasRadio">
          <input type="checkbox" v-model="pickedToArchive" class="c-library-book__archive-radio--checkbox">
          <div
            class="c-library-book__archive-radio__circle"
            :class="{'c-library-book__archive-radio--white': pickedToArchive}"
            @click.prevent="toggleArchive"
            @keyup.enter="toggleArchive"
            @keyup.space="toggleArchive"
            role="button"
            tabindex="0"
            :aria-label="`${$t('SELECT_TO_ARCHIVE')}`"
            :aria-selected="pickedToArchive ? 'true' : 'false'">
            <div class="c-library-book__archive-radio__check" v-if="pickedToArchive">
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="c-library-book__content">
      <router-link :to="{ name: 'Book', params: { id: book.id }}" class="c-library-book__link">
        <h2 class="c-library-book__title" :aria-label="titleStatusLabel">{{ book.title | truncate(100) }}</h2>
      </router-link>
      <div class="c-library-book__content-wrapper">
        <div v-if="!isDesktop">
          <button v-if="status !== 'OFFLINE_READY' && status !== 'ARCHIVING'" @click.prevent="download(book.id, book.url)" class="c-library-book__btn c-library-book__btn--bottom">
            <span class="c-library_book__btn-fill--bg" :style="`width: calc(100% - ${percentDownloaded}%)`">
            </span>
            <span class="c-library_book__btn-fill" :style="`width: ${percentDownloaded}%`" v-if="status === 'DOWNLOADING'">
              <span class="c-library-book__text--bold" :style="`left: ${downloadingTextPos}px`">{{ $t('BOOK_DOWNLOADING') }}</span>
            </span>
            <span class="c-library-book__text">
              <span class="c-library-book__text--bold" v-show="status === 'ONLINE'">{{ $t('BOOK_DOWNLOAD') }}</span>
              <span class="c-library-book__text--bold" v-show="status === 'DOWNLOADING'" ref="bookDownloadingText">{{ $t('BOOK_DOWNLOADING') }}</span>
              <span class="c-library-book__text--bold c-library-book__text--processing" v-show="status === 'PROCESSING'">{{ $t('PROCESSING') }}</span>
            </span>
          </button>
          <router-link v-else :to="{ name: 'Book', params: { id: book.id }}" class="c-library-book__link c-library-book__link--btn c-library-book__btn--bottom c-library-book__btn c-library-book__btn--downloaded">
            <span
              v-if="onBooksDelete"
              class="c-library-book__text--bold"
            >
              {{ $t('DELETING') }}
            </span>
            <span
              v-else
              class="c-library-book__text--bold"
            >
              {{ $t('BOOK_READ') }}
            </span>
          </router-link>
        </div>
        <div v-else>
          <router-link :to="{ name: 'Book', params: { id: book.id }}" class="c-library-book__link c-library-book__btn--bottom c-library-book__btn c-library-book__btn--downloaded">
            <span
              class="c-library-book__text--bold"
            >
              {{ $t('BOOK_READ') }}
            </span>
          </router-link>
        </div>
        <div class="c-library-book__size-info">
          {{ book.size | prettyBytes(1) }}
        </div>
      </div>
    </div>
    <library-book-download v-if="isDesktop" @click.native.prevent :status="status" :progress="percentDownloaded" @action-click="download(book.id, book.url)"></library-book-download>
    <library-book-options v-if="isDesktop" @click.native.prevent :status="status" @action-click="downloadOrArchive" :cover="coverHeight"></library-book-options>
  </div>
</template>

<script>
import Vue from 'vue'
import vueFilterPrettyBytes from 'vue-filter-pretty-bytes'
import { mapState } from 'vuex'

import fs from '@/services/fs'
import hpub from '@/services/fetch'

import LibraryBookDownload from '@/components/LibraryBookDownload'
import LibraryBookOptions from '@/components/LibraryBookOptions'

Vue.use(vueFilterPrettyBytes)

export default {
  name: 'LibraryBook',

  data () {
    return {
      status: 'ONLINE',
      percentDownloaded: 0,
      pickedToArchive: false,
      statusBarInterval: null,
      fallbackProgressBar: false,
      downloadingTextPos: 0,
      coverHeight: 0
    }
  },

  components: {
    'library-book-download': LibraryBookDownload,
    'library-book-options': LibraryBookOptions
  },

  computed: {
    onBooksDelete() {
      if (this.isDesktop) {
        return this.bookArchive.currentStatus === 'ARCHIVING'
      }
      return this.bookArchive.selectedBooks.indexOf(this.book.id) > -1 && this.bookArchive.currentStatus === 'ARCHIVING'
    },

    hasRadio() {
      return !this.isDesktop && this.bookArchive.currentStatus !== 'READY_TO_ARCHIVE' && this.status === 'OFFLINE_READY'
    },

    isDesktop() {
      return this.responsive === 'desktop'
    },

    bookStatusUpdate() {
      let statusUpdate

      switch(this.status) {
        case 'ONLINE':
          statusUpdate = `${this.book.title} ${this.$t('IS_AVAILABLE_ONLINE')}.`
          break
        case 'DOWNLOADING':
          statusUpdate = `${this.$t('DOWNLOADING')} ${this.book.title}`
          break
        case 'OFFLINE_READY':
          statusUpdate = `${this.book.title} ${this.$t('IS_AVAILABLE_OFFLINE')}.`
          break
        case 'ARCHIVING':
          statusUpdate = `${this.$t('ARCHIVING')} ${this.book.title}`
          break
        default:
          statusUpdate = ''
      }

      return statusUpdate
    },

    titleStatusLabel() {
      return this.bookStatusUpdate
    },

    ...mapState('userSettings', {
      responsive: state => state.responsive
    })
  },

  watch: {
    status(val) {
      if (val === 'DOWNLOADING') {
        if (this.isDesktop) {
          return
        }
        this.$nextTick(() => {
          this.downloadingTextPos = this.$refs.bookDownloadingText.offsetParent.offsetLeft
        })
      } else {
        clearInterval(this.statusBarInterval)
      }
    },

    onBooksDelete(val) {
      if (val && this.bookArchive.selectedBooks.indexOf(this.book.id) !== -1) {
        this.status = 'ARCHIVING'
      }
    },

    availableOffline() {
      this.getStatus()
    }
  },

  props: {
    book: Object,
    savedBooks: Array,
    getSavedBooks: Function,
    isOnline: Boolean,
    bookArchive: Object,
    availableOffline: Boolean
  },

  beforeMount() {
    this.getStatus()
  },

  methods: {
    toggleArchive() {
      this.pickedToArchive = !this.pickedToArchive

      if (this.pickedToArchive) {
        this.bookArchive.addToSelected(this.book.id)
      } else {
        this.bookArchive.removeFromSelected(this.book.id)
      }
    },

    downloadOrArchive() {
      if (this.availableOffline) {
        this.bookArchive.addToSelected(this.book.id)
        this.bookArchive.archiveSelected()
      } else {
        this.download(this.book.id, this.book.url)
      }
    },

    async download (id, url) {
      // Allow only books with ONLINE status, to be dowloaded
      if (this.status !== 'ONLINE') {
        return
      }

      const setNewStatus = (status, percentDownloaded) => {
        this.status = status
        this.percentDownloaded = percentDownloaded
        this.fallbackProgressBar = false
      }
      const self = this

      try {
        this.status = 'DOWNLOADING'

        await hpub.downloadHpub(id, url, {
          newStatus(status) {
            setNewStatus(status, 100)
          },
          progressUpdate(percentDownloaded, totalLength) {
            if (!totalLength) {
              if (!self.fallbackProgressBar) {
                self.statusBarInterval = setInterval(() => { self.percentDownloaded = self.percentDownloaded < 100 ? self.percentDownloaded + 5 : self.percentDownloaded }, self.book.size > 150000000 ? 3000 : 2000)
                self.fallbackProgressBar = true
              }
              // Returns null if fetch progress is not supported
              return
            }

            self.percentDownloaded = percentDownloaded
          }
        })
        await this.getSavedBooks()
        this.status = 'OFFLINE_READY'
      } catch(e) {
        setNewStatus('ONLINE', 0)
        await fs.removeBook(this.book.id)
        throw new Error(e)
      }
    },

    getStatus () {
      if (this.availableOffline) {
        this.status = 'OFFLINE_READY'
      } else {
        this.status = 'ONLINE'
      }
    },

    findCoverSize(e) {
      if (!e.target) return
      this.coverHeight = e.target.offsetHeight
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_vars.scss';
@import '@/assets/scss/_mixin.scss';
@import '@/assets/scss/_breakpoints.scss';

.c-library-book-download__status {
  @include srOnly();
}

.c-library-book {
  height: 100%;
}

.c-library-book__archive-radio {
  position: absolute;
  top: -1.375em;
  right: -1.375em;
}

.c-library-book__archive-radio__check {
  position: absolute;
  left: 0.75em;
  top: 50%;
  transform: translateY(-50%);
}

.c-library-book__archive-radio__check:after {
  content: '';
  position: absolute;
  top: -14px;
  width: 15px;
  height: 23px;
  border: solid $orange;
  border-width:0 5px 5px 0;
  transform: rotate(45deg);
}

.c-library-book__archive-radio__circle {
  @include btnReset();
  position: relative;
  width: 2.75em;
  height: 2.75em;
  background: rgba($white, 0.4);
  border-radius: 50%;
  border: 3px solid $dark-blue;
  cursor: pointer;

  @include a11yOutline();
}

.c-library-book__archive-radio--white {
  background: $white;
}

.c-library-book__archive-radio--checkbox {
  display: none;
}

.c-library-book__image-wrapper {
  position: relative;
  height: 180px;
  width: auto;
  text-align: center;
}

.c-library-book__image {
  display: block;
  margin: 0 auto;
  bottom: 0;
  left: 0;
  right: 0;
}

.c-library-book__image--bookside {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 3px;
  background: #333;
  opacity: 0.6;
  border-top-left-radius: 2px;
}

.c-library-book__image--bookline {
  position: absolute;
  top: 0;
  left: 10px;
  height: 100%;
  width: 1px;
  background: #333;
  opacity: 0.4;
}

.c-library-book__image--bookbottom {
  position: absolute;
  bottom: 0;
  left: 2px;
  right: 0;
  width: calc(100% - 2px);
  height: 2px;
  background: #333;
  opacity: 0.6;
  border-top-left-radius: 2px;
}

.c-library-book__content {
  height: calc(100% - 180px);
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 1em 1em 0 1em;
}

.c-library-book__content-wrapper {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: flex-end;
}

.c-library-book__title {
  font-size: 0.875em;
  font-weight: 700;
  letter-spacing: -0.1px;
  color: $dark-blue;
  line-height: 1.3;
  min-height: 2em;
  display: inline-block;
  max-width: 14em;
  margin-bottom: 1em;
}

.c-library-book__size-info {
  color: $dark-blue;
  font-size: 0.875rem;

  margin-top: 0.4rem;
}

.c-library-book__link {
  &--btn {
    border: 0!important;
  }
  &--image {
    display: block;
  }

  @include a11yOutline();
}

.c-library-book__actions {
  @include bookShadow();
  bottom: 0;
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0);
}

.c-library-book__btn {
  display: block;
  position:relative;
  margin: 0 auto;
  appearance: none;
  border: 2px solid $dark-blue;
  color: $dark-blue;
  border-radius: 2em;
  padding: 0.5em 0.5em;
  background: $dark-blue;
  font-size: 0.7em;
  cursor: pointer;
  min-width: 12em;
  width: 100%;
  outline: none;
  @include a11yOutline();

  &::-moz-focus-inner {
    border: 0;
  }
}

.c-library-book__btn--bottom {
  margin-top: auto;
  overflow: hidden;
}

.c-library-book__btn--downloaded {
  background: $dark-blue;
  color: $white;
  &:hover {
    background: $lighter-dark-blue;
    border-color: $lighter-dark-blue;
  }
}
.c-library-book__text {
    position: relative;
    z-index: 2;

    &--processing {
      color: $white;
    }

    &--size {
      margin-left: 4px;
    }
}
.c-library-book__text--bold {
  font-weight: 700;
  line-height: 1;
}
.c-library_book__btn-fill--bg {
  background: $white;
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  z-index: 1
}
.c-library_book__btn-fill {
  background: $dark-blue;
  color: $white;
  width: 25%;
  position: absolute;
  white-space: nowrap;
  left: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
  line-height: 1;
  z-index: 3;
  padding: 0.59em 0;

  .c-library-book__text--bold {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);

    padding: 0!important;
  }
}

@media (min-width: 555px) {
  .c-library-book__image-wrapper {
    height: 180px;
  }

  .c-library-book__btn {
    font-size: 0.8em;
  }
}

@include tablet {
  .c-library_book__btn-fill {
    padding: 0.55em 0;
    .c-library-book__text--bold {
      padding-left: 1.75em;
    }
  }

  .c-library-book__btn {
    max-width: 13em;
  }
}

@include desktop {
  .c-library_book__btn-fill {
    padding: 0.55em 0;
    .c-library-book__text--bold {
      padding-left: 2.2em;
    }
  }

  .c-library-book__btn {
    max-width: 14em;
  }
}
</style>
